import React, { useRef, useState, useEffect, useCallback } from "react";
import Webcam from "react-webcam";
import jsQR from "jsqr";
import { Box, Skeleton, Container, Typography } from "@mui/material";
import { useNavigate } from 'react-router-dom';

const QRScanner = () => {
    const webcamRef = useRef(null);
    const [scanning, setScanning] = useState(true);
    const [videoReady, setVideoReady] = useState(false);
    const [cameraKey, setCameraKey] = useState(0); // Force Webcam re-mount
    const navigate = useNavigate();

    // QR scan logic
    const captureFrame = useCallback(() => {
        if (
            webcamRef.current &&
            webcamRef.current.video &&
            webcamRef.current.video.readyState === 4
        ) {
            const video = webcamRef.current.video;
            const canvas = document.createElement("canvas");
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const code = jsQR(imageData.data, canvas.width, canvas.height);

            if (code) {
                console.log("Scanned:", code.data); // You can handle this however you want
                // alert("Scanned QR Code: " + code.data);

                // setScanning(false);

                if (!code?.data) return alert("Invalid QR Code");

                // 1. Regex to extract URLs from the string
                const urlRegex = /https?:\/\/[^\s]+/g;
                const links = code.data.match(urlRegex);

                console.log("Links:", links); // Debugging line
                if (!links) return alert("Invalid QR Code");

                // 2. Filter links that match the same domain
                const sameDomainLinks = links

                // 3. Navigate to the first valid same-domain link
                if (sameDomainLinks.length > 0) {
                    const localPath = new URL(sameDomainLinks[0]).pathname;
                    navigate(localPath);
                } else {
                    alert("Invalid QR Code")
                }

            }
        }
    }, []);

    // Scan loop
    useEffect(() => {
        let interval;
        if (scanning && videoReady) {
            interval = setInterval(captureFrame, 500);
        }
        return () => clearInterval(interval);
    }, [captureFrame, scanning, videoReady]);

    // When camera is ready
    const handleLoadedMetadata = () => {
        setVideoReady(true);
    };

    // Tap to focus (restart stream)
    const handleTapToFocus = () => {
        if (videoReady) {
            setVideoReady(false);
            setCameraKey(prev => prev + 1); // Forces re-mount
        }
    };

    return (
        <Box
            sx={{
                width: "100vw",
                height: "100vh",
                position: "fixed", // 🧷 Important to lock in viewport
                top: 0,
                left: 0,
                overflow: "hidden",
                touchAction: "manipulation",
            }}
        // onClick={handleTapToFocus}
        >
            <Container maxWidth="sm" sx={{
                zIndex: 100, position: "absolute",

                backgroundColor: "rgba(255, 255, 255, 0.7)", // semi-transparent white
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)", // for Safari support
            }}>
                <Typography
                    sx={{ textAlign: "left", pt: 2, color: "black" }}
                    variant="h4"
                    fontWeight="bold"
                    mb={2}
                >
                    Scan Code
                </Typography>
            </Container>
            {!videoReady && (
                <Skeleton
                    variant="rectangular"
                    animation="wave"
                    width="100vw"
                    height="100vh"
                    sx={{ position: "absolute", top: 0, left: 0, zIndex: 0 }}
                />
            )}

            <Webcam
                key={cameraKey}
                ref={webcamRef}
                audio={false}
                screenshotFormat="image/png"
                // videoConstraints={{ facingMode: "environment" }}
                videoConstraints={{
                    facingMode: { ideal: "environment" }, // 👈 not "exact"
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                }}
                onLoadedMetadata={handleLoadedMetadata}
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    objectFit: "cover",
                    zIndex: 1,
                    visibility: videoReady ? "visible" : "hidden",
                }}
                onUserMediaError={(err) => {
                    console.error("Camera error:", err);
                    alert("Camera access failed. Please check permissions and try again.", err);
                }}
            />

            {/* Overlay: dark background with transparent scan area */}
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    zIndex: 2,
                    pointerEvents: "none",
                }}
            >
                {/* Dark overlay with center cutout using 4 boxes */}
                <Box sx={{ position: "absolute", width: "100%", height: "100%", zIndex: 2, pointerEvents: "none" }}>
                    {/* Top */}
                    <Box sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "calc(50% - 125px)",
                        backgroundColor: "rgba(0,0,0,0.6)",
                    }} />

                    {/* Bottom */}
                    <Box sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        width: "100%",
                        height: "calc(50% - 125px)",
                        backgroundColor: "rgba(0,0,0,0.6)",
                    }} />

                    {/* Left */}
                    <Box sx={{
                        position: "absolute",
                        top: "calc(50% - 125px)",
                        left: 0,
                        width: "calc(50% - 125px)",
                        height: "250px",
                        backgroundColor: "rgba(0,0,0,0.6)",
                    }} />

                    {/* Right */}
                    <Box sx={{
                        position: "absolute",
                        top: "calc(50% - 125px)",
                        right: 0,
                        width: "calc(50% - 125px)",
                        height: "250px",
                        backgroundColor: "rgba(0,0,0,0.6)",
                    }} />

                    {/* Optional white border for the scan area */}
                    <Box sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        width: 250,
                        height: 250,
                        transform: "translate(-50%, -50%)",
                        border: "3px solid rgba(255, 255, 255, 0.9)",
                        borderRadius: 2,
                        // boxShadow: "0 0 20px rgba(255,255,255,0.3)",
                    }} />
                </Box>

                {/* Optional white scan frame */}
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        width: 250,
                        height: 250,
                        transform: "translate(-50%, -50%)",
                        border: "3px solid rgba(255, 255, 255, 0.9)",
                        borderRadius: 2,
                        // boxShadow: "0 0 20px rgba(255,255,255,0.3)",
                        zIndex: 3,
                    }}
                />
            </Box>
        </Box>
    );
};

export default QRScanner;
